import { cn } from '@/utils/tailwind-util'

interface SummaryItemDef {
  label: string
  count: number
  amount: number
  variant?: 'default' | 'success' | 'warning' | 'destructive' | 'info'
}

interface SummaryProps {
  items: SummaryItemDef[]
}

export const Summary = ({ items }: SummaryProps) => {
  const colClass = items.length === 2
    ? 'md:grid-cols-2'
    : items.length === 3
      ? 'sm:grid-cols-3 md:grid-cols-3'
      : items.length === 4
        ? 'sm:grid-cols-2 md:grid-cols-4'
        : items.length === 5
          ? 'sm:grid-cols-3 md:grid-cols-5'
          : items.length === 6
            ? 'sm:grid-cols-3 md:grid-cols-6'
            : items.length >= 7
              ? 'sm:grid-cols-4 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7'
              : 'sm:grid-cols-1'

  return (
    <div className={`grid grid-cols-1 ${colClass} rounded-lg border bg-card overflow-hidden`}>
      {items.map((item, index) => (
        <div key={index} className={index > 0 ? 'border-l' : ''}>
          <SummaryItem {...item} />
        </div>
      ))}
    </div>
  )
}

interface SummaryItemProps {
  label: string
  count: number
  amount: number
  variant?: 'default' | 'success' | 'warning' | 'destructive' | 'info'
}

const variantStyles: Record<string, string> = {
  default: 'bg-muted text-foreground',
  success: 'bg-success/10 text-success',
  warning: 'bg-warning/10 text-warning',
  destructive: 'bg-destructive/10 text-destructive',
  info: 'bg-blue-50 text-blue-600',
}

const variantAmountStyles: Record<string, string> = {
  default: 'text-muted-foreground/80',
  success: 'text-success/80',
  warning: 'text-warning/80',
  destructive: 'text-destructive/80',
  info: 'text-blue-500',
}

const SummaryItem = ({ label, count, amount, variant = 'default' }: SummaryItemProps) => (
  <div className={cn('px-4 py-2 flex flex-col justify-center', variantStyles[variant])}>
    <span className="text-[10px] font-medium uppercase tracking-wide">
      {label}
    </span>
    <div className="flex items-baseline gap-2 mt-0.5">
      <span className="text-xl font-bold">{count.toLocaleString('es-AR')}</span>
      <span className={cn('text-sm', variantAmountStyles[variant])}>
        {new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(amount)}
      </span>
    </div>
  </div>
)
